/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface ActiveAlertAcknowledgementProps {
  id: string;
  activeAlertId: string;
  dateAcknowledged: Date;
  acknowledgedById: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class ActiveAlertAcknowledgementRecord extends Record<ActiveAlertAcknowledgementProps>({
  id: null as any,
  activeAlertId: null as any,
  dateAcknowledged: null as any,
  acknowledgedById: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    activeAlertId: string;
    dateAcknowledged: string | number | Date;
    acknowledgedById: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): ActiveAlertAcknowledgementRecord {
    if (json == null) {
      throw new Error('ActiveAlertAcknowledgementRecord.fromJS: parameter cannot be null');
    }
    return new ActiveAlertAcknowledgementRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'ActiveAlertAcknowledgementJson.id',
      ),
      activeAlertId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'activeAlertId', '00000000-0000-0000-0000-000000000000'),
        'ActiveAlertAcknowledgementJson.activeAlertId',
      ),
      dateAcknowledged: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'dateAcknowledged', '0001-01-01T00:00:00.00Z'),
          'ActiveAlertAcknowledgementJson.dateAcknowledged',
        ),
      ),
      acknowledgedById: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'acknowledgedById', '00000000-0000-0000-0000-000000000000'),
        'ActiveAlertAcknowledgementJson.acknowledgedById',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'ActiveAlertAcknowledgementJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    activeAlertId: string;
    dateAcknowledged: string | number | Date;
    acknowledgedById: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [ActiveAlertAcknowledgementRecord, Error[]] {
    if (json == null) {
      throw new Error('ActiveAlertAcknowledgementRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new ActiveAlertAcknowledgementRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'ActiveAlertAcknowledgementJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      activeAlertId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'activeAlertId', '00000000-0000-0000-0000-000000000000'),
            'ActiveAlertAcknowledgementJson.activeAlertId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateAcknowledged: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'dateAcknowledged', '0001-01-01T00:00:00.00Z'),
              'ActiveAlertAcknowledgementJson.dateAcknowledged',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acknowledgedById: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'acknowledgedById', '00000000-0000-0000-0000-000000000000'),
            'ActiveAlertAcknowledgementJson.acknowledgedById',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'ActiveAlertAcknowledgementJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: ActiveAlertAcknowledgementProps) {
    super(props);
    if (props == null) {
      throw new Error('ActiveAlertAcknowledgementRecord constructor parameter cannot be null');
    }
  }
}

export abstract class ActiveAlertAcknowledgementViewModelBase
  implements ActiveAlertAcknowledgementProps
{
  protected readonly _props: ActiveAlertAcknowledgementProps;
  private _json?: ActiveAlertAcknowledgementProps;

  get id(): string {
    return this._props.id;
  }
  get activeAlertId(): string {
    return this._props.activeAlertId;
  }
  get dateAcknowledged(): Date {
    return this._props.dateAcknowledged;
  }
  get acknowledgedById(): string {
    return this._props.acknowledgedById;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: ActiveAlertAcknowledgementProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        activeAlertId: this._props.activeAlertId,
        dateAcknowledged: this._props.dateAcknowledged,
        acknowledgedById: this._props.acknowledgedById,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllActiveAlertAcknowledgementEntities = (state: AppState) =>
  state.entityStores.activeAlertAcknowledgement.entities;

export const selectActiveAlertAcknowledgementById = (id: string) => (state: AppState) =>
  state.entityStores.activeAlertAcknowledgement.entities.get(id);

export const selectActiveAlertAcknowledgementMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.activeAlertAcknowledgement.meta.get(id);

export const selectActiveAlertAcknowledgementAndMetadataById = (id: string) =>
  createSelector(
    selectActiveAlertAcknowledgementById(id),
    selectActiveAlertAcknowledgementMetadataById(id),
    (activeAlertAcknowledgement, meta) => ({ activeAlertAcknowledgement, meta }),
  );

export function selectActiveAlertAcknowledgementEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllActiveAlertAcknowledgementEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, ActiveAlertAcknowledgementRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectActiveAlertAcknowledgementEntitiesByActiveAlertId(activeAlertId: string) {
  return createSelector(selectAllActiveAlertAcknowledgementEntities, (entities) =>
    entities.filter((e) => e.activeAlertId === activeAlertId).valueSeq(),
  );
}

export function selectActiveAlertAcknowledgementEntitiesByDateAcknowledged(dateAcknowledged: Date) {
  return createSelector(selectAllActiveAlertAcknowledgementEntities, (entities) =>
    entities.filter((e) => e.dateAcknowledged === dateAcknowledged).valueSeq(),
  );
}

export function selectActiveAlertAcknowledgementEntitiesByAcknowledgedById(
  acknowledgedById: string,
) {
  return createSelector(selectAllActiveAlertAcknowledgementEntities, (entities) =>
    entities.filter((e) => e.acknowledgedById === acknowledgedById).valueSeq(),
  );
}

export function selectActiveAlertAcknowledgementEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllActiveAlertAcknowledgementEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
